import { useCallback, useEffect, useState } from 'react';

const CLIENTS_KEY = 'aariyatalk_agency_clients';
const ACTIVE_KEY = 'aariyatalk_agency_active_client';

function readClients() {
  try {
    const raw = localStorage.getItem(CLIENTS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function readActive() {
  try {
    return localStorage.getItem(ACTIVE_KEY) || '';
  } catch {
    return '';
  }
}

/**
 * Agency UX: local client labels for filtering emphasis (not multi-tenant RBAC yet).
 */
export function useAgencyClients() {
  const [clients, setClientsState] = useState(readClients);
  const [activeClientId, setActiveClientIdState] = useState(readActive);

  useEffect(() => {
    const onChange = () => {
      setClientsState(readClients());
      setActiveClientIdState(readActive());
    };
    window.addEventListener('aariyatalk-agency-clients-change', onChange);
    return () => window.removeEventListener('aariyatalk-agency-clients-change', onChange);
  }, []);

  const persist = (nextClients, activeId) => {
    localStorage.setItem(CLIENTS_KEY, JSON.stringify(nextClients));
    if (activeId !== undefined) {
      if (activeId) localStorage.setItem(ACTIVE_KEY, activeId);
      else localStorage.removeItem(ACTIVE_KEY);
    }
    window.dispatchEvent(new Event('aariyatalk-agency-clients-change'));
  };

  const setClients = useCallback((next) => {
    const list = typeof next === 'function' ? next(readClients()) : next;
    persist(list, activeClientId);
    setClientsState(list);
  }, [activeClientId]);

  const setActiveClientId = useCallback((id) => {
    persist(readClients(), id);
    setActiveClientIdState(id);
  }, []);

  const addClient = useCallback((name) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return;
    const id = `client_${Date.now()}`;
    const next = [...readClients(), { id, name: trimmed }];
    persist(next, id);
    setClientsState(next);
    setActiveClientIdState(id);
  }, []);

  const removeClient = useCallback((id) => {
    const next = readClients().filter((c) => c.id !== id);
    const nextActive = readActive() === id ? (next[0]?.id || '') : readActive();
    persist(next, nextActive);
    setClientsState(next);
    setActiveClientIdState(nextActive);
  }, []);

  const activeClient = clients.find((c) => c.id === activeClientId) || null;

  return {
    clients,
    activeClient,
    activeClientId,
    setActiveClientId,
    addClient,
    removeClient,
    setClients,
  };
}

export default useAgencyClients;
